/* ============================================================
   BLUE SALON — one variant resolver, shared by every surface

   The product card, the Quick View drawer, the wishlist drawer and the
   product detail page all have to answer the same three questions:

     - given a partial selection, which variant is that?
     - is this option value still reachable, given the rest of the picks?
     - with nothing picked yet, what should open selected?

   Each of them used to answer those itself. The rule was written out
   four times — twice in listing.js, once in product.js, once in
   wishlist.js — each copy carrying a comment claiming it matched the
   others, which is precisely how they drifted apart. The same garment
   could open on one variant in Quick View and a different one on the
   PDP: two prices, two sold-out size rows, one product. Shoppers read
   that as the site contradicting itself, and it is the reason the QA
   sheet has three separate tickets that are really one defect.

   So: one copy, loaded ahead of all four (see layout/theme.liquid).
   The rule itself is unchanged from what those four already did —
   prefer an available variant, and among those the cheapest — because
   the goal here is agreement, not new behaviour.
   ============================================================ */
(function () {
  "use strict";

  /* Variants reach us in two shapes, and both have to keep working:

       positional — v.options = ["WHITE", "XL"]
                    what Shopify publishes, and what product-card.liquid
                    and the PDP embed;
       flat       — v.color / v.size
                    what the USF search app's own cards carry, since it
                    renders its tiles from its own index rather than from
                    our Liquid.

     Everything below works on the positional shape. byField() adapts the
     flat one onto it, so there is still only one rule underneath rather
     than a second colour/size-shaped code path. */

  /* Available first, then cheapest. Not simply "first match": Shopify's
     variant order is arbitrary, so the first one is routinely sold out
     and/or full price even when the card beside it is advertising a
     cheaper in-stock size. Strictly-less-than on price, so a tie keeps
     the earlier variant and the choice stays stable between renders. */
  function pick(match) {
    if (!match.length) return null;
    var available = match.filter(function (v) { return v.available; });
    var pool = available.length ? available : match;
    return pool.reduce(function (best, v) {
      return !best || v.price < best.price ? v : best;
    }, null);
  }

  function matches(v, selected, positions) {
    for (var i = 0; i < positions.length; i++) {
      var pos = positions[i];
      var want = selected[pos];
      /* An unset position is a wildcard — that is what makes a partial
         selection resolvable at all, and what lets seed() ask "best
         variant anywhere in this product" by passing {}. */
      if (want && (v.options || [])[pos - 1] !== want) return false;
    }
    return true;
  }

  /* The resolver. selected is { position: value } and may be partial or
     empty; positions is the option positions the surface is offering. */
  function resolve(variants, selected, positions) {
    if (!variants || !variants.length) return null;
    selected = selected || {};
    positions = positions || [];
    return pick(variants.filter(function (v) {
      return matches(v, selected, positions);
    }));
  }

  /* The flat-shape entry point. Kept as its own function because it is
     exported to the USF app as window._usf_findVariant and called with
     (variants, color, size) — that signature is not ours to change. */
  function byField(variants, color, size) {
    if (!variants || !variants.length) return null;
    return pick(variants.filter(function (v) {
      return (!color || v.color === color) && (!size || v.size === size);
    }));
  }

  /* Strictly the best AVAILABLE variant, or null when the product has none
     at all. Deliberately not resolve()/byField(): those fall back to the
     cheapest sold-out variant, so a fully sold-out product still renders a
     coherent price and picker. A caller choosing a colour to repurpose a
     tile's swatch onto needs the null instead — repurposing onto a sold-out
     colour would strand the card on a combination with no stock behind it,
     which is the very thing the repurpose is there to escape. */
  function bestAvailable(variants) {
    if (!variants || !variants.length) return null;
    return variants.reduce(function (best, v) {
      if (!v.available) return best;
      return !best || v.price < best.price ? v : best;
    }, null);
  }

  /* Is `value` at `position` still buyable, holding every OTHER picked
     position fixed? This is what greys out a size row when the shopper
     switches colour. Liquid cannot answer it: product.options_with_values
     bakes value.available once, relative to whichever variant rendered
     first, so after any click it is answering about the wrong colour. */
  function valueAvailable(variants, positions, selected, position, value) {
    if (!variants || !variants.length) return false;
    positions = positions || [];
    selected = selected || {};
    return variants.some(function (v) {
      if (!v.available) return false;
      if ((v.options || [])[position - 1] !== value) return false;
      for (var i = 0; i < positions.length; i++) {
        var p = positions[i];
        if (p === position) continue;
        var want = selected[p];
        if (want && (v.options || [])[p - 1] !== want) return false;
      }
      return true;
    });
  }

  /* What a surface should open with.

     `preferred` is whatever that surface already had in hand — the
     card's active swatch, a ?variant= deep link, nothing at all. It is
     honoured when it resolves to something buyable, and dropped when it
     does not: a tile's active colour is only "whichever swatch rendered
     first", which is easily sold out in every size while the product has
     stock under the next colour along. Opening pre-loaded onto a
     guaranteed Sold Out combination, with an available one a single
     click away, is the thing this avoids.

     Returns the full selection as well as the variant, so the caller can
     seed every option — including ones it has no UI for — in one step.
     Falls back to the preferred combination only when the product truly
     has no stock anywhere, so a fully sold-out product still renders
     something coherent rather than nothing. */
  function seed(variants, positions, preferred) {
    var selected = {};
    var key;
    preferred = preferred || {};
    for (key in preferred) {
      if (Object.prototype.hasOwnProperty.call(preferred, key) && preferred[key]) {
        selected[key] = preferred[key];
      }
    }

    var variant = resolve(variants, selected, positions);
    if (!variant || !variant.available) {
      var anywhere = resolve(variants, {}, positions);
      if (anywhere && anywhere.available) {
        variant = anywhere;
        selected = {};
      }
    }

    if (variant) {
      (positions || []).forEach(function (pos) {
        if (!selected[pos]) {
          var value = (variant.options || [])[pos - 1];
          if (value) selected[pos] = value;
        }
      });
    }

    return { selected: selected, variant: variant };
  }

  /* Whether a swatch or chip should be DRAWN as sold out. The one place that
     rule lives, because three surfaces had grown three answers:

       - Quick View judged colour on its own and everything else against
         the current picks (correct, but inline in listing.js);
       - the PDP and the wishlist drawer judged colour against the current
         size, like any other option.

     The second is wrong for colour. With XXXL picked, White was crossed out
     and announced as "WHITE (Sold out)" because there is no White in XXXL —
     while White was in stock in XL, and clicking it already re-seeded onto
     White XL. The indicator contradicted what the click did. It was also a
     trap: picking XXXL crossed out White, picking White crossed out XXXL.

     Colour is the choice shoppers make first and read as a property of the
     product ("is it sold out in white?"), so it is judged across every
     size: crossed out only when no combination has stock. Every other
     option keeps the existing behaviour — judged within whatever else is
     picked — because "this size isn't available in this colour" is exactly
     what a crossed-out size should say.

     Display only. What a click does is still decided by the re-seed in each
     surface's click handler, which already lands on a buyable combination. */
  function optionAvailable(variants, positions, selected, position, value, isColor) {
    return valueAvailable(variants, positions, isColor ? {} : selected, position, value);
  }

  window.BSVariants = {
    resolve: resolve,
    byField: byField,
    bestAvailable: bestAvailable,
    valueAvailable: valueAvailable,
    optionAvailable: optionAvailable,
    seed: seed
  };
})();
